import throttle from 'lodash/throttle';
import { trackPluginCall } from '../../util/overseer/overseer';
import { AdobeMediaObject } from '../Adobe/types';
import { createMediaObject } from '../Adobe/functions/createMediaObject';
import { createAdBreakObject } from '../Adobe/functions/createAdBreakObject';
import { createAdObject } from '../Adobe/functions/createAdObject';
import { createQoEObject } from '../Adobe/functions/createQoEObject';
import type { AdobeMetadata } from '../../modules/Adobe/types';

/* eslint-disable require-jsdoc */

const PLUGIN_NAME = 'AdobeCollectionApi';

export type AdobeMediaCollectionConfig = {
  'media.playerName': string;
  'analytics.trackingServer': string;
  'analytics.reportSuite': string;
  'visitor.customerIDs'?: Record<string, any>;
  'visitor.marketingCloudOrgId': string;
  'visitor.marketingCloudUserId': string;
  'media.sdkVersion': string;
  'analytics.enableSSL': true;
  'analytics.visitorId'?: string;
};

type CollectionAPIEventType =
  | 'sessionStart'
  | 'play'
  | 'ping'
  | 'bufferStart'
  | 'pauseStart'
  | 'adBreakStart'
  | 'adStart'
  | 'adComplete'
  | 'adBreakComplete'
  | 'sessionEnd'
  | 'sessionComplete';

type PlayerTimeData = {
  ts: number;
  playhead: number;
};

type CollectionAPIEvent = {
  eventType: CollectionAPIEventType;
  playerTime?: PlayerTimeData;
  params?: Record<string, any>;
  customMetadata?: Record<string, any>;
};

export type AdobeDispatcher = ReturnType<typeof getDispatcher>;

function getDispatcher(
  trackingServer: string,
  config: AdobeMediaCollectionConfig,
  instanceData: any
) {
  let qoe: Record<string, string | number> | undefined;
  let playhead: number = 0;
  let schedulePlayEvent = false;
  let previousEvent: string;
  let sessionId: Promise<string | undefined> | undefined;
  let renewSession: () => Promise<string | undefined>;
  let lastHeartbeat: number;
  let pauseTimer: ReturnType<typeof setInterval> | undefined;

  const getPlayerTime: () => PlayerTimeData = () => ({
    playhead,
    ts: Date.now(),
  });

  const dispatchSessionStart = (event: CollectionAPIEvent) => {
    previousEvent = event.eventType;
    const url = `https://${trackingServer}/api/v1/sessions`;
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    };
    renewSession = () => {
      trackPluginCall(PLUGIN_NAME, 'renew', event);
      return fetch(url, options)
        .then(response => {
          const location = response.headers.get('location');
          return location?.replace('/api/v1/sessions/', '') ?? undefined;
        })
        .catch(() => {
          return undefined;
        });
    };
    sessionId = renewSession();
  };

  const dispatchEvent = (event: CollectionAPIEvent) => {
    if (!renewSession) return;
    if (!sessionId) sessionId = renewSession();
    previousEvent = event.eventType;

    return sessionId.then(sid => {
      if (!sid) return;

      const url = `https://${trackingServer}/api/v1/sessions/${sid}/events `;
      const options: RequestInit = {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      };
      trackPluginCall(PLUGIN_NAME, event.eventType, event);
      return fetch(url, options).catch(() => {});
    });
  };

  const dispatchPlayerTime = (eventType: CollectionAPIEventType) => {
    const event: any = {
      eventType,
      playerTime: getPlayerTime(),
    };
    if (qoe) event.qoeData = qoe;
    dispatchEvent(event);
  };

  const dispatchAdbreakStart = (params: any) => {
    dispatchEvent({
      eventType: 'adBreakStart',
      params,
      playerTime: getPlayerTime(),
    });
  };

  const dispatchAdStart = (params: any) => {
    dispatchEvent({
      eventType: 'adStart',
      params,
      playerTime: getPlayerTime(),
    });
  };

  const dispatchPlayerTimeThrottled = throttle(dispatchPlayerTime, 10000, {
    leading: false,
    trailing: true,
  });

  const stopPauseTimer = () => {
    if (pauseTimer) {
      clearInterval(pauseTimer);
      pauseTimer = undefined;
    }
  };

  const startPauseTimer = () => {
    if (pauseTimer) return;
    pauseTimer = setInterval(() => {
      const now = Date.now();

      if (now - lastHeartbeat >= 1800000) {
        sessionId = undefined;
        stopPauseTimer();
        return;
      }
      dispatchPlayerTime('ping');
    }, 10000);
  };

  return {
    trackSessionStart(media: AdobeMediaObject, metadata: AdobeMetadata): void {
      dispatchSessionStart({
        eventType: 'sessionStart',
        params: { ...removeNullEntries(media), ...instanceData, ...config },
        playerTime: getPlayerTime(),
        customMetadata: removeNullEntries(metadata),
      });
    },
    trackSessionEnd(): void {
      dispatchPlayerTimeThrottled.cancel();
      stopPauseTimer();
      dispatchPlayerTime('sessionEnd');
    },
    trackComplete(): void {
      dispatchPlayerTimeThrottled.cancel();
      stopPauseTimer();
      dispatchPlayerTime('sessionComplete');
    },
    trackEvent(event: string, data: any): void {
      switch (event) {
        case 'play': {
          schedulePlayEvent = true;
          stopPauseTimer();
          return;
        }
        case 'seekStart': {
          dispatchPlayerTimeThrottled.cancel();
          startPauseTimer();
          return;
        }
        case 'pause':
          dispatchPlayerTimeThrottled.cancel();
          dispatchPlayerTime('pauseStart');
          startPauseTimer();
          return;
        case 'adBreakStart': {
          dispatchPlayerTimeThrottled.cancel();
          dispatchAdbreakStart(data);
          return;
        }
        case 'adStart': {
          dispatchAdStart(data);
          return;
        }
        case 'adComplete': {
          dispatchPlayerTime('adComplete');
          return;
        }
        case 'adBreakComplete': {
          dispatchPlayerTime('adBreakComplete');
          return;
        }
        case 'bufferStart':
          startPauseTimer();
          dispatchPlayerTime('bufferStart');
          return;
        case 'bufferComplete': {
          schedulePlayEvent = true;
          return;
        }
        case 'seekComplete':
          schedulePlayEvent = true;
          return;
        default:
          // eslint-disable-next-line no-console
          console.log('unhandled event', event, data);
      }
    },
    updateQoEObject(qoeObject: any) {
      qoe = qoeObject;
    },
    updatePlayhead(ph: number) {
      stopPauseTimer();
      lastHeartbeat = Date.now();
      playhead = ph;

      if (schedulePlayEvent) {
        schedulePlayEvent = false;
        // multiple related inputs can cause 'play' but it should only fire once in a row
        if (previousEvent === 'play') return;
        dispatchPlayerTimeThrottled.cancel();
        return dispatchPlayerTime('play');
      }
      dispatchPlayerTimeThrottled('ping');
    },
  };
}

export type AdobeCollectionAPIInstance = ReturnType<typeof AdobeCollectionAPI>;

export type AdobeCollectionAPIOptions = {
  trackingServer: string;
  config: AdobeMediaCollectionConfig;
};
export default function AdobeCollectionAPI(options: AdobeCollectionAPIOptions) {
  if (!options.trackingServer) throw new Error('missing trackingServer url');
  if (!options.config) throw new Error('missing config');

  const getInstance = (i: any) => getDispatcher(options.trackingServer, options.config, i);
  return {
    getInstance,
    delegate() {},
    heartbeat: {
      getInstance,
      createMediaObject,
      createAdBreakObject,
      createAdObject: (adName: string, adId: string, adPosition: number, adLength: number) =>
        createAdObject(adName, adId, adPosition, adLength, options.config['media.playerName']),
      createQoEObject,
      StreamType: { Live: 'live', VOD: 'vod', Linear: 'linear' },
      MediaType: { Video: 'video', Audio: 'audio' },
      Event: {
        AdBreakComplete: 'adBreakComplete',
        AdBreakStart: 'adBreakStart',
        AdComplete: 'adComplete',
        AdSkip: 'adSkip',
        AdStart: 'adStart',
        BitrateChange: 'bitrateChange',
        BufferComplete: 'bufferComplete',
        BufferStart: 'bufferStart',
        ChapterComplete: 'chapterComplete',
        ChapterSkip: 'chapterSkip',
        ChapterStart: 'chapterStart',
        SeekComplete: 'seekComplete',
        SeekStart: 'seekStart',
        StateEnd: 'stateEnd',
        StateStart: 'stateStart',
      },
    },
  };
}

function removeNullEntries(obj: Record<string, any>) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null && v !== undefined));
}
